import { TagColor } from '@factorialco/deprecated-design-system'
import {
  BankingAccountDetailedFragment,
  BankingBankAccountFragment
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { bankAccountLastDigits, bankLogo, bankingAccountActions } from 'modules/banking/utils'

import { AccountsPageCard } from 'modules/banking/components/AccountsPageCard'

interface AccountCardProps {
  account: BankingAccountDetailedFragment
  bankAccount: BankingBankAccountFragment
  onCardClick: (account: BankingBankAccountFragment) => void
}

type SpecializedCardProps = {
  logo: JSX.Element
  title: string
  description: string
  onCardClick: () => void
  bankAccount: BankingBankAccountFragment
}

const ExpiredAccount = ({ logo, title, description, onCardClick }: SpecializedCardProps) => {
  const statusType = TagColor.radical
  const status = i18n.t('banking.accounts.status.expired')
  const linkText = i18n.t('banking.accounts.reconnect_bank')
  return (
    <AccountsPageCard
      logo={logo}
      statusType={statusType}
      status={status}
      title={title}
      description={description}
      linkText={linkText}
      onClick={onCardClick}
    />
  )
}

const PendingAccount = ({
  logo,
  title,
  description,
  bankAccount,
  onCardClick
}: SpecializedCardProps) => {
  const statusType = TagColor.lime
  const status = bankAccountLastDigits(bankAccount)
  const linkText = i18n.t('banking.accounts.finish_setup')

  return (
    <AccountsPageCard
      logo={logo}
      statusType={statusType}
      status={status}
      title={title}
      description={description}
      linkText={linkText}
      onClick={onCardClick}
    />
  )
}

const Account = ({ logo, title, description, onCardClick, bankAccount }: SpecializedCardProps) => {
  const {
    access: { locale }
  } = useCurrentSession()
  const statusType = TagColor.lime
  const status = bankAccountLastDigits(bankAccount)
  const footerText = formattedAmount({
    amountCents: bankAccount.accountBalanceCents,
    currency: bankAccount.currency.toUpperCase(),
    locale
  })

  return (
    <AccountsPageCard
      logo={logo}
      statusType={statusType}
      status={status}
      title={title}
      description={description}
      footerText={footerText}
      onClick={onCardClick}
    />
  )
}

export const AccountCard = ({ account, bankAccount, onCardClick }: AccountCardProps) => {
  const { hasToFinishSetup, hasToReconnect } = bankingAccountActions(account, bankAccount)
  const legalEntityName = account.legalEntity.legalName || ''
  const institution = account.institution
  const bankName = institution?.name
  const title = bankAccount.accountAlias || bankName || ''
  const logo = bankLogo(account, bankAccount.accountAlias ?? bankName)
  const onClick = useUnsafeCallback(() => {
    onCardClick(bankAccount)
  }, [onCardClick, bankAccount])

  if (hasToReconnect) {
    return (
      <ExpiredAccount
        logo={logo}
        title={title}
        description={legalEntityName}
        bankAccount={bankAccount}
        onCardClick={onClick}
      />
    )
  } else if (hasToFinishSetup) {
    return (
      <PendingAccount
        logo={logo}
        title={title}
        description={legalEntityName}
        bankAccount={bankAccount}
        onCardClick={onClick}
      />
    )
  } else {
    return (
      <Account
        logo={logo}
        title={title}
        description={legalEntityName}
        bankAccount={bankAccount}
        onCardClick={onClick}
      />
    )
  }
}
